import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { DownloadSimple, X, DeviceMobile } from '@phosphor-icons/react';

const DISMISS_KEY = 'install-prompt-dismissed-at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // re-show after 7 days

function isStandalone() {
  if (typeof window === 'undefined') return false;
  // Android / Chrome
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari
  if (window.navigator.standalone) return true;
  return false;
}

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function wasDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return ts && Date.now() - ts < DISMISS_TTL_MS;
  } catch (e) {
    return false;
  }
}

/**
 * Install prompt banner.
 * - Android / Chrome: catches `beforeinstallprompt` and exposes a button.
 * - iOS Safari: no programmatic prompt; shows "Add to Home Screen" instructions.
 * - Hidden entirely when the app is already running standalone.
 */
const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (isStandalone() || wasDismissed()) return;

    // Android/Chrome path
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS Safari path: no event; only show once per session if not standalone.
    if (isIOS()) {
      setVisible(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  // The banner floats over the bottom of the page, which on the gate is
  // exactly where the sign-in button sits. Publishing its height lets a page
  // make room below its content (see Gate.js), so nothing it covers becomes
  // unreachable; 0px whenever it is not showing.
  useEffect(() => {
    const root = document.documentElement;
    const el = box.current;
    if (!visible || !el) {
      root.style.setProperty('--bottom-overlay', '0px');
      return undefined;
    }
    const publish = () => root.style.setProperty('--bottom-overlay', `${el.offsetHeight}px`);
    publish();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    if (ro) ro.observe(el);
    return () => {
      if (ro) ro.disconnect();
      root.style.setProperty('--bottom-overlay', '0px');
    };
  }, [visible, showIOSHelp]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) { /* ignore */ }
    setVisible(false);
    setShowIOSHelp(false);
  }, []);

  const onInstallClick = useCallback(async () => {
    if (isIOS()) {
      setShowIOSHelp(true);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } finally {
      setDeferred(null);
      setVisible(false);
    }
  }, [deferred]);

  if (!visible) return null;

  return (
    <div
      ref={box}
      className="fixed bottom-0 left-0 right-0 z-50 safe-bottom"
      data-testid="install-prompt"
    >
      <div className="mx-auto max-w-md m-3 p-4 rounded-xl border border-border bg-card shadow-lg">
        {!showIOSHelp ? (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <DeviceMobile className="w-5 h-5 text-primary" weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">Install Arcanum</p>
              <p className="text-xs text-muted-foreground truncate">
                Add Arcanum to your home screen for quick access
              </p>
            </div>
            <Button
              size="sm"
              onClick={onInstallClick}
              className="bg-primary hover:bg-primary/90"
              data-testid="install-button"
            >
              <DownloadSimple className="w-4 h-4 mr-1" />
              Install
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={dismiss}
              className="compact-button h-8 w-8"
              aria-label="Dismiss"
              data-testid="install-dismiss"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-foreground">Add to Home Screen</p>
              <Button
                size="icon"
                variant="ghost"
                onClick={dismiss}
                className="compact-button h-6 w-6"
                aria-label="Dismiss"
                data-testid="install-close-ios"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
              <li>Tap the Share button in Safari</li>
              <li>Scroll and choose <span className="font-medium text-foreground">Add to Home Screen</span></li>
              <li>Tap <span className="font-medium text-foreground">Add</span> in the top right</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;
